import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable, Share } from 'react-native';
import Head from 'expo-router/head';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Gift,
  Users,
  Copy,
  Share2,
  Check,
  Star,
  Percent,
  Clock,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import * as Clipboard from 'expo-clipboard';
import { useThemeColors } from '@/lib/useThemeColors';
import { mode } from '@/lib/config/app-mode';
import { useProfileStore } from '@/lib/state/profile-store';
import { CURRENCY } from '@/lib/pricing';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout, Breadcrumb } from '@/components/AppLayout';
import AnimatedPressable from '@/components/AnimatedPressable';

// Referral rewards configuration
const STARTUP_REWARDS = {
  referrerDiscount: 20, // 20% off next month
  refereeDiscount: 10, // 10% off first month
  freeReports: 1, // 1 free detailed report
};

const INVESTOR_REWARDS = {
  referrerDiscount: 15, // 15% off next month
  refereeDiscount: 10, // 10% off first month
  freeReports: 0,
};

interface Referral {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'registered' | 'subscribed';
  date: string;
  rewardEarned?: string;
}

// Mock referral data
const mockReferrals: Referral[] = [
  { id: '1', name: 'Alex Thompson', email: 'alex@startup.io', status: 'subscribed', date: '2025-01-10', rewardEarned: '20% discount' },
  { id: '2', name: 'Maria Garcia', email: 'maria@tech.co', status: 'registered', date: '2025-01-08' },
  { id: '3', name: 'James Wilson', email: 'james@venture.com', status: 'pending', date: '2025-01-12' },
];

export default function ReferralScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const userRole = useProfileStore((s) => s.userRole);
  const user = useProfileStore((s) => s.user);
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const [copied, setCopied] = useState(false);

  const isStartup = userRole === 'startup';
  // No referrals table exists — only `waitlist.referral_code`, which powers
  // waitlist position, not the in-app referral programme. So this list is
  // sample-only by necessity and the UI says so below, rather than showing an
  // empty list that implies nobody has taken up your code.
  const referrals = mode.showSampleData ? mockReferrals : [];

  // Dynamic breadcrumbs based on user role
  const breadcrumbs: Breadcrumb[] = isStartup
    ? [
        { label: 'Company Profile', href: '/(startup)/company' },
        { label: 'Referral Program' },
      ]
    : [
        { label: 'Profile', href: '/(investor)/investorprofile' },
        { label: 'Referral Program' },
      ];
  const rewards = isStartup ? STARTUP_REWARDS : INVESTOR_REWARDS;
  // For startups, could use company or person name. For investors, use user.name
  const userName = isStartup ? (user?.name || startupProfile?.name) : user?.name;

  // Generate a mock referral code
  const referralCode = `INVES-${(userName ?? 'USER').substring(0, 4).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  const referralLink = `https://inverio.ai/join?ref=${referralCode}`;

  const stats = {
    totalReferrals: referrals.length,
    successfulReferrals: referrals.filter(r => r.status === 'subscribed').length,
    pendingRewards: referrals.filter(r => r.status === 'registered').length,
    totalEarned: `${CURRENCY} 40`,
  };

  const handleCopyCode = async () => {
    await Clipboard.setStringAsync(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    try {
      await Share.share({
        message: `Join me on Inverio - the AI-powered investment platform!\n\nUse my referral code: ${referralCode}\n\nGet ${rewards.refereeDiscount}% off your first month!\n\n${referralLink}`,
        title: 'Inverio Referral',
      });
    } catch (error) {
      // Share cancelled or failed
    }
  };

  const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
    pending: { color: colors.warning, bg: colors.amber.light, label: 'Pending' },
    registered: { color: colors.primary[600], bg: colors.primary[50], label: 'Registered' },
    subscribed: { color: colors.success, bg: colors.traffic.greenLight, label: 'Subscribed' },
  };
  // `referral.status` is rehydrated from the persisted `inverio-referrals` store,
  // which outlives any single release — a value written by an older build can
  // land in a map that no longer has that key, and `status.label` would throw.
  const statusFallback = { color: colors.text.tertiary, bg: colors.light[300], label: 'Unknown' };

  const content = (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      {isWeb && (
        <Head>
          <title>Referral Program — Inverio</title>
          <meta name="description" content="Invite others to Inverio and earn rewards. Share your referral code with startups and investors." />
          <link rel="canonical" href="https://inverio.ai/referral" />
          <meta property="og:title" content="Referral Program — Inverio" />
          <meta property="og:description" content="Invite others to Inverio and earn rewards. Share your referral code with startups and investors." />
          <meta property="og:url" content="https://inverio.ai/referral" />
        </Head>
      )}
      <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
        {/* Header - Only show on mobile */}
        {phoneShell && (
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <View className="flex-row items-center gap-4">
            <AnimatedPressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </AnimatedPressable>
            <View>
              <Text style={{ color: colors.text.secondary }} className="text-sm">Earn Rewards</Text>
              <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">Referral Program</Text>
            </View>
          </View>
        </Animated.View>
        )}

        <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Hero Card */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="mx-5 mt-6 mb-6"
            >
              <View
                className="rounded-3xl p-6"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <View className="flex-row items-center gap-3 mb-6">
                  <View
                    className="w-12 h-12 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Gift size={24} color={colors.primary[600]} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.primary }} className="text-xl font-bold">Invite Friends, Earn Rewards</Text>
                    <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
                      Both you and your friend get discounts!
                    </Text>
                  </View>
                </View>

                <View className="flex-row gap-3 mb-4">
                  <View
                    className="flex-1 rounded-2xl p-4"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">You Get</Text>
                    <Text style={{ color: colors.primary[600] }} className="text-lg font-bold">{rewards.referrerDiscount}% OFF</Text>
                    <Text style={{ color: colors.text.secondary }} className="text-xs">Next month</Text>
                  </View>
                  <View
                    className="flex-1 rounded-2xl p-4"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Friend Gets</Text>
                    <Text style={{ color: colors.primary[600] }} className="text-lg font-bold">{rewards.refereeDiscount}% OFF</Text>
                    <Text style={{ color: colors.text.secondary }} className="text-xs">First month</Text>
                  </View>
                </View>

                {isStartup && (
                  <View
                    className="rounded-2xl p-4 mb-4"
                    style={{ backgroundColor: colors.traffic.greenLight }}
                  >
                    <View className="flex-row items-center gap-2">
                      <Star size={16} color={colors.success} />
                      <Text style={{ color: colors.success }} className="font-medium">Bonus: {rewards.freeReports} Free Detailed Report</Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-xs mt-1">
                      Per successful referral subscription
                    </Text>
                  </View>
                )}

                {/* Referral Code */}
                <View
                  className="rounded-2xl p-4 border"
                  style={{ backgroundColor: colors.surface.secondary, borderColor: colors.border.light }}
                >
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mb-2 text-center">Your Referral Code</Text>
                  <View className="flex-row items-center justify-between">
                    <Text style={{ color: colors.text.primary }} className="text-xl font-bold tracking-wider">{referralCode}</Text>
                    <AnimatedPressable
                      onPress={handleCopyCode}
                      className="w-10 h-10 rounded-full items-center justify-center"
                      style={{ backgroundColor: colors.primary[50] }}
                    >
                      {copied ? <Check size={20} color={colors.primary[600]} /> : <Copy size={20} color={colors.primary[600]} />}
                    </AnimatedPressable>
                  </View>
                </View>
              </View>
            </Animated.View>

            {/* Share Button */}
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="mx-5 mb-6"
            >
              <AnimatedPressable
                onPress={handleShare}
                className="rounded-2xl p-4"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <View className="flex-row items-center justify-center gap-3">
                  <Share2 size={20} color="white" />
                  <Text style={{ color: colors.text.inverse }} className="font-semibold text-lg">Share Invite Link</Text>
                </View>
              </AnimatedPressable>
            </Animated.View>

            {/* Stats */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(500)}
              className="px-5 mb-6"
            >
              <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-4">Your Stats</Text>
              <View className="flex-row gap-3">
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <Users size={20} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">{stats.totalReferrals}</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs">Total Referrals</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.traffic.greenLight }}
                >
                  <Check size={20} color={colors.success} />
                  <Text style={{ color: colors.success }} className="text-2xl font-bold mt-2">{stats.successfulReferrals}</Text>
                  <Text style={{ color: colors.success }} className="text-xs">Successful</Text>
                </View>
              </View>
              <View className="flex-row gap-3 mt-3">
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.amber.light }}
                >
                  <Clock size={20} color={colors.warning} />
                  <Text style={{ color: colors.warning }} className="text-2xl font-bold mt-2">{stats.pendingRewards}</Text>
                  <Text style={{ color: colors.warning }} className="text-xs">Pending</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <Percent size={20} color={colors.primary[600]} />
                  <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold mt-2">{stats.totalEarned}</Text>
                  <Text style={{ color: colors.primary[600] }} className="text-xs">Total Saved</Text>
                </View>
              </View>
            </Animated.View>

            {/* Referral History */}
            <Animated.View
              entering={FadeInUp.delay(500).duration(500)}
              className="px-5 pb-8"
            >
              <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mb-4">Referral History</Text>
              {/* Honest absence. Rendering an empty list under this heading
                  implies nobody has used your code; in production the list is
                  empty because referral tracking is not built, which is a very
                  different statement. */}
              {referrals.length === 0 && (
                <View
                  className="p-5 rounded-2xl"
                  style={{ backgroundColor: colors.surface.primary }}
                >
                  <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                    Referral tracking isn&apos;t live yet. Your code works and
                    referrals still count — they just won&apos;t be itemised here
                    until tracking ships.
                  </Text>
                </View>
              )}
              <View className="gap-3">
                {referrals.map((referral) => {
                  const status = statusConfig[referral.status] ?? statusFallback;
                  return (
                    <View
                      key={referral.id}
                      className="p-4 rounded-2xl"
                      style={{ backgroundColor: colors.surface.primary }}
                    >
                      <View className="flex-row items-center justify-between">
                        <View className="flex-row items-center gap-3">
                          <View
                            className="w-10 h-10 rounded-full items-center justify-center"
                            style={{ backgroundColor: colors.primary[50] }}
                          >
                            <Text style={{ color: colors.primary[600] }} className="font-bold">
                              {referral.name.split(' ').map(n => n[0]).join('')}
                            </Text>
                          </View>
                          <View>
                            <Text style={{ color: colors.text.primary }} className="font-medium">{referral.name}</Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-xs">{referral.date}</Text>
                          </View>
                        </View>
                        <View
                          className="px-3 py-1.5 rounded-full"
                          style={{ backgroundColor: status.bg }}
                        >
                          <Text style={{ color: status.color }} className="text-xs font-medium">
                            {status.label}
                          </Text>
                        </View>
                      </View>
                      {referral.rewardEarned && (
                        <View
                          className="mt-3 pt-3 border-t flex-row items-center gap-2"
                          style={{ borderColor: colors.border.light }}
                        >
                          <Gift size={14} color={colors.success} />
                          <Text style={{ color: colors.success }} className="text-xs font-medium">
                            Earned: {referral.rewardEarned}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            </Animated.View>

            {/* Terms */}
            <Animated.View
              entering={FadeInUp.delay(600).duration(500)}
              className="px-5 pb-8"
            >
              <View
                className="rounded-2xl p-4"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <Text style={{ color: colors.text.primary }} className="text-sm font-semibold mb-2">
                  How It Works
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-xs leading-relaxed">
                  1. Share your unique referral code with friends{'\n'}
                  2. They sign up and subscribe using your code{'\n'}
                  3. You both receive discounts automatically{'\n'}
                  4. No limit on number of referrals!
                </Text>
              </View>
            </Animated.View>
        </ScrollView>
      </SafeAreaView>
    </View>
  );

  // Wrap with AppLayout for web
  if (isWeb) {
    return (
      <AppLayout title="Referral Program" breadcrumbs={breadcrumbs}>
        {content}
      </AppLayout>
    );
  }

  return content;
}
